import React from 'react';
import { IconType } from 'react-icons';
import ChakraIcon from '../components/common/ChakraIcon';

import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Stack,
  Button,
  Divider,
  VStack,
  HStack,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Icon,
  Image,
  useBreakpointValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaMapMarkerAlt, 
  FaEnvelope, 
  FaPhone, 
  FaClock,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin
} from 'react-icons/fa';

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);
const MotionHeading = motion(Heading);

const Contact: React.FC = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } }
  };
  
  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };
  
  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Contact info data
  const contactInfo = [
    {
      icon: FaMapMarkerAlt,
      title: 'Address',
      content: 'Kasindi, Basongora groupement, Beni territory, North Kivu Province, DRC',
      color: 'brand.500'
    },
    {
      icon: FaEnvelope,
      title: 'Email',
      content: 'shalomeprojects@gmail.com',
      color: 'green.500'
    },
    {
      icon: FaPhone,
      title: 'Phone',
      content: '+243 975 622 685',
      color: 'earth.500'
    },
    {
      icon: FaClock,
      title: 'Office Hours',
      content: 'Monday - Friday: 8:00 AM - 5:00 PM',
      color: 'brand.500'
    }
  ];

  // Social media data
  const socialMedia = [
    {
      icon: FaFacebook,
      name: 'Facebook',
      url: 'https://facebook.com',
      color: '#1877F2'
    },
    {
      icon: FaTwitter,
      name: 'Twitter',
      url: 'https://twitter.com',
      color: '#1DA1F2'
    },
    {
      icon: FaInstagram,
      name: 'Instagram',
      url: 'https://instagram.com',
      color: '#E4405F'
    },
    {
      icon: FaLinkedin,
      name: 'LinkedIn',
      url: 'https://linkedin.com',
      color: '#0A66C2'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        position="relative"
        h={{ base: '40vh', md: '50vh' }}
        bgImage="linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/assets/images/photo_2025-05-04_15-33-20.jpg')"
        bgPosition="center"
        bgSize="cover"
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="container.xl" textAlign="center">
          <MotionHeading
            as={motion.h1}
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="bold"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            Contact Us
          </MotionHeading>
          
          <MotionText
            fontSize={{ base: 'lg', md: 'xl' }}
            mt={4}
            maxW="800px"
            mx="auto"
            initial="hidden"
            animate="visible"
            variants={slideUp}
          >
            We'd love to hear from you. Reach out with questions, partnership inquiries, or to learn more about our work.
          </MotionText>
        </Container>
      </Box>

      {/* Contact Information and Form */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionFlex
            direction={{ base: 'column', lg: 'row' }}
            align="stretch"
            justify="space-between"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Contact Information */}
            <MotionBox
              flex="1"
              mr={{ base: 0, lg: 10 }}
              mb={{ base: 10, lg: 0 }}
              variants={slideUp}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
                color="brand.500"
              >
                Get in Touch
              </Heading>
              
              <Divider w="100px" borderColor="brand.500" borderWidth="2px" mb={6} />
              
              <Text fontSize={{ base: 'md', md: 'lg' }} mb={8}>
                We're here to answer any questions you may have about our work, partnership opportunities, or how you can get involved.
              </Text>
              
              <VStack spacing={6} align="stretch" mb={10}>
                {contactInfo.map((item, index) => (
                  <HStack key={index} spacing={4} align="flex-start">
                    <Flex
                      w="50px"
                      h="50px"
                      borderRadius="full"
                      bg={`${item.color}10`}
                      color={item.color}
                      justify="center"
                      align="center"
                    >
                      <ChakraIcon icon={item.icon} boxSize="20px" />
                    </Flex>
                    
                    <Box>
                      <Heading as="h3" fontSize="lg" mb={1}>
                        {item.title}
                      </Heading>
                      <Text color="gray.600">
                        {item.content}
                      </Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
              
              <Box>
                <Heading as="h3" fontSize="lg" mb={4}>
                  Follow Us
                </Heading>
                
                <HStack spacing={4}>
                  {socialMedia.map((social, index) => (
                    <Flex
                      key={index}
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg={social.color}
                      color="white"
                      justify="center"
                      align="center"
                      cursor="pointer"
                      transition="all 0.3s ease"
                      _hover={{ transform: 'translateY(-3px)' }}
                      as="a"
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name}
                    >
                      <ChakraIcon icon={social.icon} boxSize="20px" />
                    </Flex>
                  ))}
                </HStack>
              </Box>
            </MotionBox>
            
            {/* Contact Form */}
            <MotionBox
              flex="1"
              bg="white"
              p={8}
              borderRadius="lg"
              boxShadow="md"
              variants={slideUp}
            >
              <Heading as="h2" fontSize="2xl" mb={6}>
                Send Us a Message
              </Heading>
              
              <VStack spacing={6}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
                  <FormControl isRequired>
                    <FormLabel>First Name</FormLabel>
                    <Input placeholder="Your first name" />
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel>Last Name</FormLabel>
                    <Input placeholder="Your last name" />
                  </FormControl>
                </SimpleGrid>
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input placeholder="Your email address" type="email" />
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Phone</FormLabel>
                    <Input placeholder="Your phone number (optional)" />
                  </FormControl>
                </SimpleGrid>
                
                <FormControl isRequired w="full">
                  <FormLabel>Subject</FormLabel>
                  <Input placeholder="Subject of your message" />
                </FormControl>
                
                <FormControl isRequired w="full">
                  <FormLabel>Message</FormLabel>
                  <Textarea placeholder="Your message" rows={5} />
                </FormControl>
                
                <Button
                  variant="primary"
                  size="lg"
                  w="full"
                >
                  Send Message
                </Button>
              </VStack>
            </MotionBox>
          </MotionFlex>
        </Container>
      </Box>

      {/* Map Section */}
      <Box py={{ base: 12, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={12}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Our Location
            </Heading>
            
            <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              ALPHA is based in Kasindi, Basongora groupement, Beni territory, North Kivu Province, Democratic Republic of Congo.
            </Text>
          </MotionBox>
          
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={slideUp}
            borderRadius="lg"
            overflow="hidden"
            boxShadow="md"
          >
            <Box position="relative" h="400px">
              <Image
                src="/assets/images/photo_2025-05-04_15-35-39.jpg"
                alt="Map of ALPHA's location in Kasindi, DRC"
                w="100%"
                h="100%"
                objectFit="cover"
              />
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                bg="brand.500"
                color="white"
                p={3}
                borderRadius="md"
                boxShadow="lg"
              >
                <Flex align="center">
                  <ChakraIcon icon={FaMapMarkerAlt} boxSize="20px" mr={2} />
                  <Text fontWeight="bold">ALPHA Headquarters</Text>
                </Flex>
              </Box>
            </Box>
          </MotionBox>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={12}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Frequently Asked Questions
            </Heading>
            
            <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              Find answers to commonly asked questions about our organization and work.
            </Text>
          </MotionBox>
          
          <MotionFlex
            as={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={staggerChildren}
            direction="column"
            gap={6}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <MotionBox variants={slideUp}>
                <VStack align="flex-start" spacing={4}>
                  <Heading as="h3" fontSize="lg" color="brand.500">
                    How can I donate to ALPHA?
                  </Heading>
                  <Text>
                    You can donate through our website, by bank transfer, or by contacting our office directly. We accept both one-time and recurring donations.
                  </Text>
                </VStack>
              </MotionBox>
              
              <MotionBox variants={slideUp}>
                <VStack align="flex-start" spacing={4}>
                  <Heading as="h3" fontSize="lg" color="brand.500">
                    Can I volunteer remotely?
                  </Heading>
                  <Text>
                    Yes, we have remote volunteer opportunities in areas such as digital support, content creation, and translation. Contact us to learn more about current remote positions.
                  </Text>
                </VStack>
              </MotionBox>
              
              <MotionBox variants={slideUp}>
                <VStack align="flex-start" spacing={4}>
                  <Heading as="h3" fontSize="lg" color="brand.500">
                    How are donations used?
                  </Heading>
                  <Text>
                    Donations support our programs in education, community development, advocacy, and mental health support. We ensure transparency in the use of all funds received.
                  </Text>
                </VStack>
              </MotionBox>
              
              <MotionBox variants={slideUp}>
                <VStack align="flex-start" spacing={4}>
                  <Heading as="h3" fontSize="lg" color="brand.500">
                    How can organizations partner with ALPHA?
                  </Heading>
                  <Text>
                    We welcome partnerships with organizations that share our vision and values. Please contact us to discuss potential collaboration opportunities.
                  </Text>
                </VStack>
              </MotionBox>
            </SimpleGrid>
          </MotionFlex>
          
          <MotionBox
            textAlign="center"
            mt={12}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={slideUp}
          >
            <Text fontSize="lg" mb={4}>
              Don't see your question here?
            </Text>
            <Button
              variant="primary"
              size="lg"
            >
              Contact Us
            </Button>
          </MotionBox>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;
